import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import {
  ColorInputFormat,
  ColorSwatch,
  ColorSwatchAppliedDelayMs,
  ColorSwatchPopoverGap,
  ColorSwatchPopoverWidth,
  ColorToneStops,
  EssentialColorSwatches,
} from '../../_models';
import { ColorSwatchService } from '../../_services';

@Component({
  selector: 'app-color-swatch-picker',
  templateUrl: './color-swatch-picker.component.html',
  styleUrls: ['./color-swatch-picker.component.scss'],
})
export class ColorSwatchPickerComponent implements OnDestroy {
  @Input() color: string;
  @Input() label = '';
  @Output() colorChange = new EventEmitter<string>();
  @Input() hideTrigger = false;
  @Input() allowRemove = false;
  @Output() closed = new EventEmitter<boolean>();
  @Output() removed = new EventEmitter<void>();

  colorFormat = ColorInputFormat;
  format = ColorInputFormat.Hex;
  isOpen = false;
  applied = false;
  toneBase: string = null;
  popoverStyle: { [key: string]: string } = {};
  private originalColor: string = null;
  private anchor: HTMLElement = null;
  private appliedTimer: any = null;
  private repositionListener = () => this.reposition();

  constructor(private elementRef: ElementRef, public swatchService: ColorSwatchService) {}

  get swatches(): ColorSwatch[] {
    return [...this.swatchService.brandColors, ...EssentialColorSwatches];
  }

  get tones(): string[] {
    let tones = [];
    if (this.toneBase && this.toRgba(this.toneBase)) {
      tones = ColorToneStops.map((weight) => this.mix(this.toneBase, weight));
      // Very light/dark bases collapse to the same shade at the ends; show each tone once.
      tones = tones.filter((tone, index) => tones.indexOf(tone) === index);
    }
    return tones;
  }

  get rgba(): number[] {
    let rgba = [255, 255, 255, 1];
    if (this.color && this.toRgba(this.color)) {
      rgba = this.toRgba(this.color);
    }
    return rgba;
  }

  get hex(): string {
    return this.fromRgba([...this.rgba.slice(0, 3), 1]).toUpperCase();
  }

  get opacity(): number {
    return Math.round(this.rgba[3] * 100);
  }

  get toneLabel(): string {
    const brand = this.swatchService.brandColors.find((swatch) => swatch.color === this.toneBase);
    return brand ? brand.label : '';
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isOpen) {
      this.cancel();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent) {
    const inAnchor = this.anchor && this.anchor.contains(event.target as Node);
    if (this.isOpen && !inAnchor && !this.elementRef.nativeElement.contains(event.target)) {
      this.close(true);
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close(true);
    } else {
      this.open();
    }
  }

  // Tones follow brand colors only; Black / White / custom / tone picks keep the current tones row.
  selectColor(color: string) {
    if (this.swatchService.brandColors.some((swatch) => swatch.color === color)) {
      this.toneBase = color;
    }
    this.setColor(color);
  }

  setColor(color: string) {
    if (color !== this.color) {
      this.color = color;
      this.colorChange.emit(color);
    }
  }

  isSelected(color: string): boolean {
    return !!this.color && this.color.toLowerCase() === color.toLowerCase();
  }

  // Inputs apply on change; invalid entries snap back to the current color.
  onHexChange(input: HTMLInputElement) {
    const parsed = this.toRgba('#' + input.value.replace('#', ''));
    if (parsed) {
      this.setColor(this.fromRgba([...parsed.slice(0, 3), this.rgba[3]]));
    }
    input.value = this.hex;
  }

  onChannelChange(index: number, input: HTMLInputElement) {
    this.updateRgba(index, this.clamp(input.value, 255));
    input.value = String(this.rgba[index]);
  }

  onOpacityChange(input: HTMLInputElement) {
    this.updateRgba(3, this.clamp(input.value, 100) / 100);
    input.value = String(this.opacity);
  }

  saveCustom() {
    this.swatchService.addCustomColor(this.color);
  }

  removeCustom(color: string, event: MouseEvent) {
    event.stopPropagation();
    this.swatchService.removeCustomColor(color);
  }

  cancel() {
    this.setColor(this.originalColor);
    this.close(false);
  }

  removeColor() {
    this.removed.emit();
    this.close(false);
  }

  // Opens the popover next to an external element (e.g. a TinyMCE toolbar button) instead of the built-in field.
  openAt(anchor: HTMLElement) {
    this.anchor = anchor;
    this.open();
  }

  // Closes keeping the current color, as Done / clicking outside do.
  dismiss() {
    this.close(true);
  }

  done() {
    this.applied = true;
    this.appliedTimer = setTimeout(() => this.close(true), ColorSwatchAppliedDelayMs);
  }

  ngOnDestroy() {
    clearTimeout(this.appliedTimer);
    document.removeEventListener('scroll', this.repositionListener, true);
  }

  private open() {
    clearTimeout(this.appliedTimer);
    this.swatchService.refresh();
    const brand = this.swatchService.brandColors.find((swatch) => this.isSelected(swatch.color));
    this.originalColor = this.color;
    this.applied = false;
    this.toneBase = brand ? brand.color : null;
    if (!this.toneBase && this.swatchService.brandColors.length) {
      this.toneBase = this.swatchService.brandColors[0].color;
    }
    this.isOpen = true;
    this.reposition();
    document.addEventListener('scroll', this.repositionListener, true);
  }

  // Keeps a new color as a custom swatch unless the user cancelled or it's already offered.
  private close(keepColor: boolean) {
    const known = [...this.swatches.map((swatch) => swatch.color), ...this.swatchService.customColors, ...this.tones];
    if (
      keepColor &&
      this.isOpen &&
      this.color &&
      this.color !== this.originalColor &&
      !known.some((color) => this.isSelected(color))
    ) {
      this.swatchService.addCustomColor(this.color);
    }
    if (this.isOpen) {
      this.closed.emit(keepColor);
    }
    clearTimeout(this.appliedTimer);
    this.isOpen = false;
    this.applied = false;
    this.anchor = null;
    document.removeEventListener('scroll', this.repositionListener, true);
  }

  // Fixed positioning so scrollable sidebars don't clip the popover. Opens on the side of the trigger with more room
  // and is capped to that room (scrolls inside) so it never runs off short screens.
  private reposition() {
    const rect = (this.anchor || this.elementRef.nativeElement).getBoundingClientRect();
    const gap = ColorSwatchPopoverGap;
    const maxLeft = window.innerWidth - ColorSwatchPopoverWidth - gap;
    const left = Math.max(gap, Math.min(rect.left, maxLeft)) + 'px';
    const spaceBelow = window.innerHeight - rect.bottom - gap * 2;
    const spaceAbove = rect.top - gap * 2;
    this.popoverStyle = { left: left, top: rect.bottom + gap + 'px', maxHeight: spaceBelow + 'px' };
    if (spaceAbove > spaceBelow) {
      this.popoverStyle = { left: left, bottom: window.innerHeight - rect.top + gap + 'px', maxHeight: spaceAbove + 'px' };
    }
  }

  private updateRgba(index: number, value: number) {
    const rgba = [...this.rgba];
    rgba[index] = value;
    this.setColor(this.fromRgba(rgba));
  }

  private clamp(value: string, max: number): number {
    return Math.min(max, Math.max(0, Math.round(Number(value) || 0)));
  }

  // Parses #rgb / #rrggbb / #rrggbbaa and rgb() / rgba() into [r, g, b, a] (a in 0-1); null if unrecognised.
  private toRgba(color: string): number[] {
    let rgba = null;
    let hex = color.trim().replace('#', '');
    const fn = /^rgba?\(([^)]+)\)$/i.exec(color.trim());
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('');
    }
    if (/^([0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) {
      rgba = [0, 2, 4].map((index) => parseInt(hex.substr(index, 2), 16));
      rgba.push(hex.length === 8 ? parseInt(hex.substr(6, 2), 16) / 255 : 1);
    } else if (fn) {
      rgba = fn[1].split(',').map((part) => Number(part));
      if (rgba.length === 3) {
        rgba.push(1);
      }
    }
    return rgba;
  }

  // Same output as the picker's 'auto' format: hex when opaque, rgba() otherwise.
  private fromRgba(rgba: number[]): string {
    let color =
      '#' +
      rgba
        .slice(0, 3)
        .map((channel) => ('0' + channel.toString(16)).slice(-2))
        .join('');
    if (rgba[3] < 1) {
      color = 'rgba(' + rgba.slice(0, 3).join(',') + ',' + Math.round(rgba[3] * 100) / 100 + ')';
    }
    return color;
  }

  // Mixes toward white (negative weight) or black (positive weight).
  private mix(color: string, weight: number): string {
    const target = weight < 0 ? 255 : 0;
    const channels = this.toRgba(color)
      .slice(0, 3)
      .map((channel) => Math.round(channel + (target - channel) * Math.abs(weight)));
    return this.fromRgba([...channels, 1]);
  }
}
